/* ══════════════════════════════════════════════════════════════════════════
   WhatsApp — enlaces y disponibilidad
   ──────────────────────────────────────────────────────────────────────────
   Un solo lugar para armar los enlaces wa.me, de modo que cada botón del
   sitio pueda llevar un mensaje pre-escrito según su contexto (el visitante
   abre WhatsApp con el mensaje listo y sólo aprieta enviar: menos fricción y
   nos dice de dónde viene el contacto).

   Los clics se registran solos en GA4 / Google Ads: analytics.ts escucha por
   delegación cualquier <a> cuyo href sea de wa.me. No hay que anotar nada.
   ══════════════════════════════════════════════════════════════════════════ */

import { WA_NUMBER } from '../data/constants';
import { codigoAtribucion } from './atribucion';

/* Arma el enlace de WhatsApp con un mensaje pre-escrito (texto plano).

   Si la persona llegó por un anuncio, el mensaje termina con un código corto
   de origen. WhatsApp es la acción principal del sitio y una conversación de
   WhatsApp no pasa por el formulario, así que no queda registrada en el panel
   ni en Ads: sin este código no hay forma de saber qué campaña la trajo. El
   tráfico orgánico no lleva código y ve el mensaje limpio de siempre. */
export function waLink(mensaje?: string): string {
  const base = `https://wa.me/${WA_NUMBER}`;
  if (!mensaje) return base;
  const codigo = codigoAtribucion();
  const texto = codigo ? `${mensaje}\n\n[ref: ${codigo}]` : mensaje;
  return `${base}?text=${encodeURIComponent(texto)}`;
}

/* Horario de atención declarado en el sitio: lunes a viernes, 09:00–18:00.
   Se evalúa en hora de Chile continental y no en la hora local del visitante,
   para no prometer "respondemos ahora" a alguien que nos escribe de noche. */
const HORA_APERTURA = 9;
const HORA_CIERRE = 18;

/** Hora y día de la semana en Santiago, independiente del reloj del visitante. */
function ahoraEnSantiago(): { hora: number; dia: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Santiago',
    hour: 'numeric',
    hour12: false,
    weekday: 'short',
  });
  const partes = fmt.formatToParts(new Date());
  const hora = Number(partes.find((p) => p.type === 'hour')?.value ?? 0);
  const dias = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dia = dias.indexOf(partes.find((p) => p.type === 'weekday')?.value ?? 'Sun');
  return { hora, dia };
}

/** true si estamos dentro del horario de atención declarado. */
export function enHorarioAtencion(): boolean {
  const { hora, dia } = ahoraEnSantiago();
  return dia >= 1 && dia <= 5 && hora >= HORA_APERTURA && hora < HORA_CIERRE;
}

/** Promesa de respuesta honesta según la hora: fija la expectativa correcta. */
export function promesaDeRespuesta(): string {
  return enHorarioAtencion()
    ? 'Respondemos en pocos minutos'
    : 'Escríbenos ahora y te respondemos a primera hora';
}
